import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  MessageSquareIcon,
  MailIcon,
  SmartphoneIcon,
  PlusIcon,
  ExternalLinkIcon,
  SendIcon,
  Loader2Icon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { sendSms, normalizePhoneDigits, formatPhoneForDisplay } from '../lib/sendSms'

export type ConversationEntityType = 'customer' | 'property' | 'contract' | 'job' | 'lead' | 'employee' | 'proposal'

interface ConversationsPanelProps {
  entityType: ConversationEntityType
  entityId: string
  /** Customer to associate new conversations with, if different from entityId itself. */
  customerId?: string | null
  className?: string
}

type Channel = 'whatsapp' | 'sms' | 'email'

const CHANNEL_META: Record<Channel, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  whatsapp: { label: 'WhatsApp', color: 'text-green-600', bg: 'bg-green-50', icon: MessageSquareIcon },
  sms: { label: 'SMS', color: 'text-blue-600', bg: 'bg-blue-50', icon: SmartphoneIcon },
  email: { label: 'Email', color: 'text-orange-600', bg: 'bg-orange-50', icon: MailIcon },
}

function relativeTime(dateStr: string) {
  const diffMs = Date.now() - new Date(dateStr).getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'now'
  if (diffMin < 60) return `${diffMin}m`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h`
  const d = new Date(dateStr)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Compact, reusable conversation list + quick-compose box scoped to a single
 * entity (customer/property/contract/job/etc). Embed on detail pages so
 * users can see and start conversations without leaving the record.
 */
export function ConversationsPanel({ entityType, entityId, customerId, className = '' }: ConversationsPanelProps) {
  const navigate = useNavigate()
  const [conversations, setConversations] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [composing, setComposing] = useState(false)
  const [channel, setChannel] = useState<Channel>('email')
  const [recipient, setRecipient] = useState('')
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [smsFromNumbers, setSmsFromNumbers] = useState<string[]>([])
  const [smsFrom, setSmsFrom] = useState('')
  const [senderId, setSenderId] = useState<string | null>(null)
  const [senderName, setSenderName] = useState<string | null>(null)

  useEffect(() => {
    fetchConversations()
    fetch('/api/config/client')
      .then((r) => r.json())
      .then((cfg) => {
        setSmsFromNumbers(cfg.smsFromNumbers || [])
        setSmsFrom(cfg.smsDefaultFrom || cfg.smsFromNumbers?.[0] || '')
      })
      .catch(() => {})
    ;(async () => {
      const { data: session } = await supabase.auth.getUser()
      const uid = session.user?.id || null
      setSenderId(uid)
      if (!uid) return
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('first_name, last_name, email')
        .eq('id', uid)
        .maybeSingle()
      const name = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim()
      setSenderName(name || profile?.email || null)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId])

  const fetchConversations = async () => {
    try {
      setLoading(true)
      const { data } = await supabase
        .from('conversations')
        .select('*')
        .eq(`${entityType}_id`, entityId)
        .in('state', ['active', 'archived']) // hide spam / trash on entity pages
        .order('last_message_at', { ascending: false })
        .limit(10)
      setConversations(data || [])
    } catch {
      setConversations([])
    } finally {
      setLoading(false)
    }
  }

  const handleSend = async () => {
    if (!recipient.trim() || !body.trim()) return
    setSending(true)
    try {
      // SMS threads store the normalized number so inbound webhook replies match the thread
      const contactInfo =
        channel === 'sms' ? normalizePhoneDigits(recipient.trim()) || recipient.trim() : recipient.trim()

      const { data: convo, error } = await supabase
        .from('conversations')
        .insert({
          channel,
          contact_name: recipient.trim(),
          contact_info: contactInfo,
          last_message: body.trim().slice(0, 200),
          last_message_at: new Date().toISOString(),
          unread_count: 0,
          customer_id: customerId || (entityType === 'customer' ? entityId : null),
          [`${entityType}_id`]: entityId,
        })
        .select()
        .single()
      if (error) throw error

      const { data: sentMsg } = await supabase
        .from('messages')
        .insert({
          conversation_id: convo.id,
          channel,
          direction: 'outbound',
          contact_name: recipient.trim(),
          contact_phone: channel !== 'email' ? contactInfo : null,
          contact_email: channel === 'email' ? contactInfo : null,
          body: body.trim(),
          status: 'sent',
          customer_id: customerId || (entityType === 'customer' ? entityId : null),
          sent_by: senderId,
          sent_by_name: senderName,
        })
        .select('id')
        .single()

      if (channel === 'email') {
        try {
          const resp = await fetch('/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ to: recipient.trim(), subject: '', body: body.trim() }),
          })
          // Store the sent Message-ID so an emailed reply threads into this conversation
          const info = await resp.json().catch(() => ({} as any))
          if (resp.ok && info.messageId && sentMsg?.id) {
            await supabase.from('messages').update({ external_id: info.messageId }).eq('id', sentMsg.id)
          }
        } catch {
          // best-effort; message is already logged in the inbox
        }
      } else if (channel === 'sms') {
        try {
          await sendSms({ to: contactInfo, body: body.trim(), from: smsFrom || undefined })
        } catch (smsErr: any) {
          alert('Message logged but SMS failed: ' + (smsErr.message || 'Unknown error'))
        }
      }

      setConversations((prev) => [convo, ...prev])
      setComposing(false)
      setRecipient('')
      setBody('')
    } catch (err) {
      console.error('Failed to send message:', err)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <MessageSquareIcon className="w-4 h-4 text-gray-500" />
          Conversations
        </h3>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/messages?tab=external&entityType=${entityType}&entityId=${entityId}`)}
            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
          >
            View all
            <ExternalLinkIcon className="w-3 h-3" />
          </button>
          <button onClick={() => setComposing((v) => !v)} className="p-1 rounded hover:bg-gray-100">
            <PlusIcon className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      {composing && (
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 space-y-2">
          <div className="flex gap-1.5">
            {(['email', 'sms', 'whatsapp'] as const).map((ch) => (
              <button
                key={ch}
                onClick={() => setChannel(ch)}
                className={`px-2 py-1 text-xs rounded-lg ${
                  channel === ch ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600'
                }`}
              >
                {CHANNEL_META[ch].label}
              </button>
            ))}
          </div>
          <input
            type={channel === 'email' ? 'email' : 'tel'}
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder={channel === 'email' ? 'recipient@example.com' : '+1 (555) 000-0000'}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {channel === 'sms' && smsFromNumbers.length > 1 && (
            <select
              value={smsFrom}
              onChange={(e) => setSmsFrom(e.target.value)}
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {smsFromNumbers.map((n) => (
                <option key={n} value={n}>
                  From {formatPhoneForDisplay(n)}
                </option>
              ))}
            </select>
          )}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            placeholder="Message..."
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setComposing(false)} className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900">
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || !recipient.trim() || !body.trim()}
              className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {sending ? <Loader2Icon className="w-3 h-3 animate-spin" /> : <SendIcon className="w-3 h-3" />}
              Send
            </button>
          </div>
        </div>
      )}

      <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-xs text-gray-400">Loading...</div>
        ) : conversations.length === 0 ? (
          <div className="p-4 text-center text-xs text-gray-400">No conversations linked yet</div>
        ) : (
          conversations.map((c) => {
            const meta = CHANNEL_META[c.channel as Channel] || CHANNEL_META.email
            const Icon = meta.icon
            // Email threads are titled by subject (threading fix); other channels by contact
            const title = (c.channel === 'email' && c.subject) || c.contact_name || c.contact_info
            return (
              <button
                key={c.id}
                onClick={() => navigate(`/messages/${c.id}?tab=external`)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-2.5"
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
                  <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{title}</p>
                    {c.state === 'archived' && (
                      <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-100 text-gray-600 flex-shrink-0">
                        Archived
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{c.last_message || 'No messages yet'}</p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {c.last_message_at && (
                    <span className="text-[10px] text-gray-400">{relativeTime(c.last_message_at)}</span>
                  )}
                  {c.unread_count > 0 && <span className="w-2 h-2 bg-blue-600 rounded-full" />}
                </div>
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}

export default ConversationsPanel
